import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Typography from 'carbon-react/lib/components/typography';

import { Sidebar } from '@futrli/ui-components';

import { FormulaPrediction } from './formula-prediction/formula-prediction';
import { FreestylePrediction } from './freestyle-prediction/freestyle-prediction';
import { PredictionType } from '../../types';
import { RepeatingPrediction } from './repeating-prediction/repeating-prediction';
import { trackEvent } from '../../../../core/tracker';
import { TRACKING_EVENTS } from '../../../../core/constants';
import { UnitPrediction } from './unit-prediction/unit-prediction';
import { useConsolidatedOrganisationPredictionBuilderContext } from '../../context/consolidated-organisation-prediction-builder-context';

import styles from '../prediction-drawer.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ConsolidatedOrganisationPredictionDrawer = ({ isOpen, onClose }: Props) => {
  const { isEditing, predictionType } = useConsolidatedOrganisationPredictionBuilderContext();

  const intl = useIntl();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    trackEvent(TRACKING_EVENTS.OPEN_CONSOLIDATED_ORG_MANUAL_PREDICTION_SIDEBAR);
  }, [isOpen]);

  if (isEditing) {
    return (
      <Sidebar
        open={isOpen}
        className={styles.predictionBuilderDrawer}
        data-testid="snapshot-drawer"
        aria-label={intl.formatMessage({ id: 'predict.prediction_builder', defaultMessage: 'Prediction builder' })}
        width="85%"
        onCancel={onClose}
        header={
          <Typography variant="h2" fontWeight="700">
            <FormattedMessage id="predict.prediction_builder" defaultMessage="Prediction builder" />
          </Typography>
        }
      >
        {predictionType === PredictionType.FREESTYLE && <FreestylePrediction onClose={onClose} />}
        {predictionType === PredictionType.FORMULA && <FormulaPrediction onClose={onClose} />}
        {predictionType === PredictionType.REPEATING && <RepeatingPrediction onClose={onClose} />}
        {predictionType === PredictionType.UNIT && <UnitPrediction onClose={onClose} />}
      </Sidebar>
    );
  }

  return (
    <Sidebar
      open={isOpen}
      className={styles.predictionBuilderDrawer}
      data-testid="snapshot-drawer"
      aria-label={intl.formatMessage({ id: 'predict.prediction_builder', defaultMessage: 'Prediction builder' })}
      width="85%"
      onCancel={onClose}
      header={
        <Typography variant="h2" fontWeight="700">
          <FormattedMessage id="predict.prediction_builder" defaultMessage="Prediction builder" />
        </Typography>
      }
    >
      <FreestylePrediction onClose={onClose} isPredictionTypeActive={predictionType === PredictionType.FREESTYLE} />
      <FormulaPrediction onClose={onClose} isPredictionTypeActive={predictionType === PredictionType.FORMULA} />
      <RepeatingPrediction onClose={onClose} isPredictionTypeActive={predictionType === PredictionType.REPEATING} />
      <UnitPrediction onClose={onClose} isPredictionTypeActive={predictionType === PredictionType.UNIT} />
    </Sidebar>
  );
};
